/**
 * Contact Service
 * Frontend service for handling contact form submissions
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface ContactFormData {
  name: string;
  email: string;
  restaurant?: string;
  message: string;
  recaptchaToken?: string;
}

export interface ContactFormResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface SmsOptInData {
  phoneNumber: string;
  recaptchaToken?: string;
}

export interface SmsOptInResponse {
  success: boolean;
  isDuplicate?: boolean;
  message?: string;
  error?: string;
}

/**
 * Submit contact form data to the backend
 * @param formData - The contact form data
 * @returns Promise with submission result
 */
export const submitContactForm = async (formData: ContactFormData): Promise<ContactFormResponse> => {
  try {
    if (!formData.email || !formData.message) {
      throw new Error('Email and message are required');
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Please enter a valid email address');
    }

    // Length validation
    if (formData.message.length > 2000) {
      throw new Error('Message is too long (maximum 2000 characters)');
    }

    if (formData.name.length > 100) {
      throw new Error('Name is too long (maximum 100 characters)');
    }

    if (formData.email.length > 100) {
      throw new Error('Email is too long (maximum 100 characters)');
    }

    // Call the Cloud Function
    const submitForm = httpsCallable(functions, 'submitContactForm');
    const result = await submitForm(formData);
    
    return result.data as ContactFormResponse;
    
  } catch (error: any) {
    console.error('Contact form submission error:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to send message. Please try again.'
    };
  }
};

/**
 * Submit SMS opt-in for product updates
 */
export const submitSmsOptIn = async (data: SmsOptInData): Promise<SmsOptInResponse> => {
  try {
    const phoneDigits = data.phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      throw new Error('Please enter a valid 10-digit US phone number');
    }

    const submitOptIn = httpsCallable(functions, 'submitSmsOptIn');
    const result = await submitOptIn({
      phoneNumber: phoneDigits,
      recaptchaToken: data.recaptchaToken
    });

    return result.data as SmsOptInResponse;
  } catch (error: any) {
    console.error('SMS opt-in submission error:', error);
    return {
      success: false,
      error: error.message || 'Failed to subscribe. Please try again.'
    };
  }
};

/**
 * Get contact submissions (admin only)
 * @returns Promise with submissions data
 */
export const getContactSubmissions = async () => {
  try {
    const getSubmissions = httpsCallable(functions, 'getContactSubmissions');
    const result = await getSubmissions();
    
    return result.data;
    
  } catch (error: any) {
    console.error('Failed to get contact submissions:', error);
    throw error;
  }
};
